import { useEffect } from 'react';
import useSWR from 'swr';
import { RoleUserOrgAssociationDto } from '@/apiClient';
import { type UserProfileDto } from '@/authApiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { useGetOrganisationsByUserId } from '@/hooks/useGetOrganisationsByUserId';
import { getSessionStorageItem } from '@/lib/browserStorage';

export type UserProfileLoadedCallback = (userProfile: UserProfileDto) => void;
export type UserOrgAssociationsRetrievedCallback = (
  organisations: RoleUserOrgAssociationDto[],
) => void;

export interface PostLoginFlowProps {
  onUserProfileLoaded: UserProfileLoadedCallback;
  onUserOrgAssociationsRetrieved: UserOrgAssociationsRetrievedCallback;
}

export function PostLoginFlow({
  onUserProfileLoaded,
  onUserOrgAssociationsRetrieved,
}: PostLoginFlowProps) {
  const {
    loginFlowComplete,
    userId,
    userProfile,
    userProfileApi,
    setUserOrganisations,
  } = usePrivate();

  const { data, error: orgFetchError } = useGetOrganisationsByUserId(userId);

  const hasSubmittedSurvey =
    userProfile?.surveyResponse?.surveyResponseEntityId;
  const storedSurveySkipped = getSessionStorageItem<boolean>('surveySkipped');
  // Survey always happens after EULA selection
  const bothEulaAndSurveyDone = hasSubmittedSurvey || storedSurveySkipped;

  useEffect(() => {
    if (!setUserOrganisations) return;

    // Resolve to an empty array on fetch error so downstream consumers
    // (e.g. the registry layout) can transition out of their loading state
    // and route the user appropriately instead of hanging forever.
    if (orgFetchError) {
      setUserOrganisations([]);
      return;
    }

    if (!data) return;

    const uniqueOrganisations = data.data.reduce(
      (acc: RoleUserOrgAssociationDto[], org) => {
        if (
          !acc.some(
            (existingOrg) => existingOrg.organisationId === org.organisationId,
          )
        ) {
          acc.push(org);
        }
        return acc;
      },
      [],
    );

    setUserOrganisations(uniqueOrganisations);

    if (bothEulaAndSurveyDone) {
      onUserOrgAssociationsRetrieved(uniqueOrganisations);
    }

    //eslint-disable-next-line
  }, [data, orgFetchError]);

  const fetchUserProfile = () => {
    if (loginFlowComplete && userId && userProfileApi) {
      return userProfileApi.apiUserProfileGet(userId);
    }
  };

  const { data: userProfileResponse } = useSWR(
    userId ? `/api/UserProfile/${userId}` : null,
    fetchUserProfile,
    {
      onError: (error) => {
        console.error(
          '[BREEDLOGIC] <PostLogicFlow> Error fetching user profile',
          error,
        );
      },
    },
  );

  useEffect(() => {
    if (
      userProfileResponse?.data &&
      userProfileResponse?.data !== userProfile
    ) {
      onUserProfileLoaded(userProfileResponse.data);
    }
  }, [onUserProfileLoaded, userProfileResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
